type RefundParams = {
  totalPaid: number;          // 총 결제금액
  periodStart: string;        // "YYYY-MM-DD"
  periodEnd: string;          // "YYYY-MM-DD" (포함)
  lastAttendDate: string;     // 실제 마지막 재원일 "YYYY-MM-DD" (포함)
  roundUnit?: number;         // 기본 10 (10원 단위 내림)
  adjustRemainderToLast?: boolean; // 기본 true (잔액 마지막월 보정)
};

type MonthBreakdown = {
  key: string;               // "YYYY-MM"
  segStart: string;          // 이 월에서 결제기간이 시작되는 날짜
  segEnd: string;            // 이 월에서 결제기간이 끝나는 날짜
  monthDays: number;         // 해당월 총일수
  registeredDays: number;    // 이 월에 등록된 일수(포함)
  weight: number;            // registeredDays / monthDays
  portion: number;           // 월 배분금액(내림+잔액보정 반영)
  isFullMonthPurchased: boolean; // 이 월을 1일~말일로 “월 전체 결제”했는지
};

type RefundResult = {
  charge: number;            // 재결제/실청구 금액
  refund: number;            // 환불액 = totalPaid - charge
  appliedFractionOnLastMonth: number; // 마지막월에 적용된 청구 비율(1/3,1/2,2/3,1)
  usedDaysInLastMonth: number;        // 마지막월 실제 재원일수(포함)
  thresholds: { oneThird: number; oneHalf: number; twoThird: number }; // 마지막월 기준일수
  breakdown: MonthBreakdown[];
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function parseDateOnly(ymd: string): Date {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function fmtDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

function daysInMonth(d: Date): number {
  return endOfMonth(d).getDate();
}

function clampDate(d: Date, min: Date, max: Date): Date {
  return new Date(Math.min(Math.max(d.getTime(), min.getTime()), max.getTime()));
}

function diffDaysInclusive(a: Date, b: Date): number {
  const aa = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
  const bb = new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime();
  return Math.floor((bb - aa) / MS_PER_DAY) + 1;
}

function floorToUnit(n: number, unit: number): number {
  if (unit <= 1) return Math.floor(n);
  return Math.floor(n / unit) * unit;
}

/**
 * 마지막월에 적용할 비율 결정
 * - 1/3 기준: ceil(D/3)
 * - 1/2 기준: ceil(D/2)
 * - 2/3 기준: floor(2D/3)  (서비스)
 * - 2/3은 "월 전체 결제"일 때만 허용
 * - 1/2 초과면 원칙상 환불 없어도 되므로 => 기본은 1(=100% 청구),
 *   다만 월전체 결제면 2/3 cutoff까지는 2/3 허용
 */
function fractionByAcademyPolicy(
  usedDays: number,
  basisDays: number,
  isFullMonthPurchased: boolean
): { fraction: number; thresholds: { oneThird: number; oneHalf: number; twoThird: number } } {
  const oneThird = Math.ceil(basisDays / 3);
  const oneHalf = Math.ceil(basisDays / 2);
  const twoThird = Math.floor((2 * basisDays) / 3);

  let fraction = 1;

  if (usedDays <= oneThird) fraction = 1 / 3;
  else if (usedDays <= oneHalf) fraction = 1 / 2;
  else {
    // 원칙상(학원법) 1/2 넘으면 환불 안해줘도 됨.
    // 서비스 2/3은 "월 전체 결제"일 때만, twoThird cutoff까지 허용.
    if (isFullMonthPurchased && usedDays <= twoThird) fraction = 2 / 3;
    else fraction = 1;
  }

  return { fraction, thresholds: { oneThird, oneHalf, twoThird } };
}

export function buildMonthlyBreakdown(
  totalPaid: number,
  periodStart: Date,
  periodEnd: Date,
  roundUnit: number,
  adjustRemainderToLast: boolean
): MonthBreakdown[] {
  const s = new Date(periodStart.getFullYear(), periodStart.getMonth(), periodStart.getDate());
  const e = new Date(periodEnd.getFullYear(), periodEnd.getMonth(), periodEnd.getDate());
  if (s > e) return [];

  // 1) 월 segment 생성 (월별 등록일수 / 월 총일수)
  const segments: Omit<MonthBreakdown, "portion">[] = [];
  let cur = startOfMonth(s);
  const lastMonth = startOfMonth(e);

  while (cur.getTime() <= lastMonth.getTime()) {
    const mStart = startOfMonth(cur);
    const mEnd = endOfMonth(cur);

    const segStart = clampDate(mStart, s, e);
    const segEnd = clampDate(mEnd, s, e);

    if (segStart <= segEnd) {
      const monthDays = daysInMonth(cur);
      const registeredDays = diffDaysInclusive(segStart, segEnd);
      const weight = registeredDays / monthDays;

      // "월 전체 결제" 판단: 이 월에서 segment가 1일~말일을 모두 포함하는가
      const isFullMonthPurchased =
        segStart.getTime() === mStart.getTime() && segEnd.getTime() === mEnd.getTime();

      segments.push({
        key: `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}`,
        segStart: fmtDate(segStart),
        segEnd: fmtDate(segEnd),
        monthDays,
        registeredDays,
        weight,
        isFullMonthPurchased,
      });
    }

    cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
  }

  const sumWeight = segments.reduce((acc, x) => acc + x.weight, 0);
  if (sumWeight <= 0) {
    return segments.map((x) => ({ ...x, portion: 0 }));
  }

  // 2) 월별 배분 + 내림
  const withPortion = segments.map((x) => {
    const raw = (totalPaid * x.weight) / sumWeight;
    const portion = floorToUnit(raw, roundUnit);
    return { ...x, portion };
  });

  // 3) 내림 잔액을 마지막 월에 보정
  if (adjustRemainderToLast && withPortion.length > 0) {
    const sum = withPortion.reduce((acc, x) => acc + x.portion, 0);
    const remainder = totalPaid - sum;
    if (remainder !== 0) {
      withPortion[withPortion.length - 1] = {
        ...withPortion[withPortion.length - 1],
        portion: withPortion[withPortion.length - 1].portion + remainder,
      };
    }
  }

  return withPortion;
}

/**
 * 메인 함수: 환불/재결제 계산
 */
export function calcRefundAndCharge(params: RefundParams): RefundResult {
  const roundUnit = params.roundUnit ?? 10;
  const adjustRemainderToLast = params.adjustRemainderToLast ?? true;

  const totalPaid = Math.max(0, Math.floor(params.totalPaid || 0));
  const start = parseDateOnly(params.periodStart);
  const end = parseDateOnly(params.periodEnd);
  const lastAttendRaw = parseDateOnly(params.lastAttendDate);

  const breakdown = buildMonthlyBreakdown(totalPaid, start, end, roundUnit, adjustRemainderToLast);

  if (breakdown.length === 0) {
    return {
      charge: 0,
      refund: totalPaid,
      appliedFractionOnLastMonth: 0,
      usedDaysInLastMonth: 0,
      thresholds: { oneThird: 0, oneHalf: 0, twoThird: 0 },
      breakdown: [],
    };
  }

  // lastAttend를 기간 안으로 clamp (정책 다르면 여기 수정)
  const s = parseDateOnly(breakdown[0].segStart);
  const e = parseDateOnly(breakdown[breakdown.length - 1].segEnd);
  const lastAttend = clampDate(lastAttendRaw, s, e);

  // 마지막 재원일이 속한 월 찾기
  let lastIdx = breakdown.length - 1;
  for (let i = 0; i < breakdown.length; i++) {
    const segStart = parseDateOnly(breakdown[i].segStart);
    const segEnd = parseDateOnly(breakdown[i].segEnd);
    if (segStart <= lastAttend && lastAttend <= segEnd) {
      lastIdx = i;
      break;
    }
  }

  // 마지막월: 실제 재원일수(해당월 segStart~lastAttend 포함)
  const lastSeg = breakdown[lastIdx];
  const lastSegStart = parseDateOnly(lastSeg.segStart);
  const usedDaysInLastMonth = diffDaysInclusive(lastSegStart, lastAttend);

  // 기준일수(basisDays)는 "해당 수강권이 그 월에서 커버하는 일수"가 아니라,
  // 네가 말한 예시처럼 "그 달 자체의 총일수"로 판단하는 게 더 일관적임.
  // (1~31이면 D=31 기준으로 11/16/20이 나오게)
  const basisDays = lastSeg.registeredDays;

  const { fraction, thresholds } = fractionByAcademyPolicy(
    usedDaysInLastMonth,
    basisDays,
    lastSeg.isFullMonthPurchased
  );

  // 이전달은 100% 청구
  const beforeCharge = breakdown.slice(0, lastIdx).reduce((acc, x) => acc + x.portion, 0);

  // 마지막달은 fraction 적용 후 내림
  const lastCharge = floorToUnit(lastSeg.portion * fraction, roundUnit);

  const charge = beforeCharge + lastCharge;
  const refund = totalPaid - charge;

  return {
    charge,
    refund,
    appliedFractionOnLastMonth: fraction,
    usedDaysInLastMonth,
    thresholds,
    breakdown,
  };
}